import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Star, ShoppingCart } from "lucide-react";
import { useProducts } from "../hooks/useProducts";
import { useRealTimeProducts } from "../hooks/useRealTimeProducts";
import { productAPI } from "../services/api";
import FreeDeliveryBanner from "../components/FreeDeliveryBanner";
import toast from "react-hot-toast";

const Shop = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("name");
  const [categories, setCategories] = useState(["All"]);

  // Use React Query for products - automatic caching and refetching
  const { data, isLoading, error } = useProducts({
    limit: 100,
    isActive: true,
  });
  const products = data?.products || [];

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCategories();
  }, []);

  // Show error toast if products fail to load
  useEffect(() => {
    if (error) {
      toast.error("Failed to fetch products");
      console.error("Error fetching products:", error);
    }
  }, [error]);

  const fetchCategories = async () => {
    try {
      const response = await productAPI.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const filteredProducts = products.filter(
    (product) =>
      selectedCategory === "All" || product.category === selectedCategory
  );

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case "price-low":
        return a.price - b.price;
      case "price-high":
        return b.price - a.price;
      case "discount":
        return b.discount - a.discount;
      default:
        return a.name.localeCompare(b.name);
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#305669]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Promotional Offer Banner */}
      <FreeDeliveryBanner variant="shop" />

      {/* Header - Responsive */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 lg:px-8">
          <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900">
            Our Rice Collection
          </h1>
        </div>

        {/* Filters Bar - Sticky */}
        <div className="border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-3 py-2 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              {/* Category Filter - Horizontal Scroll */}
              <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-3 py-1.5 lg:px-4 lg:py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      selectedCategory === category
                        ? "bg-[#305669] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {/* Sort Filter */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full lg:w-auto px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#305669] focus:border-transparent bg-white"
              >
                <option value="name">Sort by Name</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="discount">Highest Discount</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid - Responsive */}
      <div className="max-w-7xl mx-auto px-2 py-3 sm:px-4 sm:py-4 lg:px-8 lg:py-6">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3 lg:gap-4">
          {sortedProducts.map((product) => (
            <div
              key={product._id}
              className="bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden flex flex-col group"
            >
              {/* Product Image */}
              <Link
                to={`/product/${product._id}`}
                className="relative aspect-square bg-gray-100"
              >
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
                {product.discount > 0 && (
                  <div className="absolute top-1 left-1 lg:top-2 lg:left-2 bg-red-500 text-white px-1.5 py-0.5 lg:px-2 lg:py-1 rounded text-xs font-semibold">
                    {product.discount}% OFF
                  </div>
                )}
              </Link>

              {/* Product Info */}
              <div className="p-2 sm:p-3 lg:p-4 flex flex-col flex-grow">
                {/* Product Name */}
                <Link to={`/product/${product._id}`}>
                  <h3 className="text-sm sm:text-base lg:text-lg font-medium text-gray-900 mb-1 line-clamp-2 min-h-[2.5rem] sm:min-h-[3rem] hover:text-[#305669]">
                    {product.name}
                  </h3>
                </Link>

                {/* Weight & Origin */}
                <p className="text-xs lg:text-sm text-gray-500 mb-2">
                  {product.weight} {product.origin && `• ${product.origin}`}
                </p>

                {/* Rating */}
                <div className="flex items-center mb-2 lg:mb-3">
                  <div className="flex items-center bg-green-600 text-white px-1.5 py-0.5 rounded text-xs font-medium">
                    <span>4.8</span>
                    <Star className="h-2.5 w-2.5 fill-current ml-0.5" />
                  </div>
                  <span className="text-xs text-gray-400 ml-1.5">
                    ({Math.floor(Math.random() * 1000) + 100})
                  </span>
                </div>

                {/* Price Section */}
                <div className="mt-auto">
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <span className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">
                      ₹{product.price}
                    </span>
                    {product.originalPrice > product.price && (
                      <>
                        <span className="text-xs sm:text-sm text-gray-400 line-through">
                          ₹{product.originalPrice}
                        </span>
                        <span className="text-xs text-green-600 font-medium">
                          {product.discount}% off
                        </span>
                      </>
                    )}
                  </div>

                  {/* Stock Status */}
                  {product.stock > 0 ? (
                    <div className="flex items-center text-xs lg:text-sm text-green-600 mb-3">
                      <div className="w-1.5 h-1.5 bg-green-600 rounded-full mr-1"></div>
                      In Stock
                    </div>
                  ) : (
                    <div className="text-xs lg:text-sm text-red-500 mb-3">
                      Out of Stock
                    </div>
                  )}

                  {/* Action Buttons - Desktop Only */}
                  <div className="hidden lg:flex gap-2">
                    <Link
                      to={`/product/${product._id}`}
                      className="flex-1 bg-[#305669] text-white py-2 px-3 rounded-lg hover:bg-[#1785A] transition-colors duration-200 text-center text-sm font-medium"
                    >
                      View Details
                    </Link>
                    <Link
                      to={`/order/${product._id}`}
                      className="flex items-center justify-center bg-[#8ABEB9] text-white py-2 px-3 rounded-lg hover:bg-[#305669] transition-colors duration-200"
                    >
                      <ShoppingCart className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {sortedProducts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-base lg:text-lg">
              No products found in this category.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Shop;
